import { IDocumentFileModel } from '@api/models';
import {
    abortMultipartUpload,
    BulkInitMultipartUploadItem,
    completeMultipartUpload,
    getPartUploadUrl,
    initMultipartUploadBulk,
    UploadPart,
} from '@api/services/document.service';
import { useProgressTabContext } from '@library/progress-tab';
import { useMutation } from '@tanstack/react-query';
import { generateUniqueId } from '@utils/common';
import axios from 'axios';
import { convertFileSize, getFileExtension } from 'modules/shared/utils/file-utils';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

interface MultipartUploadConfig {
    chunkSize: number;
    maxConcurrentUploads: number;
}

interface SubmitInput {
    files: File[];
    name: string;
    folderId: string;
    users: string[];
}

interface UploadSingleFileInput {
    file: File;
    name: string;
    folderId: string;
    users: string[];
    progressTabItemId: string;
    abortController: AbortController;
    uploadId: string;
    key: string;
}

interface AddDocumentOptions {
    clientId: string;
    onAdd: (file: IDocumentFileModel) => void;
    config?: Partial<MultipartUploadConfig>;
}

const DEFAULT_CONFIG: MultipartUploadConfig = {
    chunkSize: 200 * 1024 * 1024,
    maxConcurrentUploads: 3,
};

const useMultipartUpload = (options: AddDocumentOptions) => {
    const { onAdd, config, clientId } = options;

    const navigate = useNavigate();
    const progressTabCtx = useProgressTabContext();

    const mergedConfig = useMemo<MultipartUploadConfig>(
        () => ({
            ...DEFAULT_CONFIG,
            ...config,
        }),
        [config]
    );

    const initMultipartUploadMutation = useMutation({
        mutationFn: async ({
            files,
            name,
        }: {
            files: File[];
            name: string;
        }): Promise<BulkInitMultipartUploadItem[]> => {
            return initMultipartUploadBulk(clientId, {
                files: files.map((file) => ({
                    name: files.length > 1 ? file.name : name || file.name,
                    fileSizeInBytes: file.size,
                    contentType: file.type || 'application/octet-stream',
                    extension: getFileExtension(file.name),
                })),
            });
        },
    });

    const uploadFileMutation = useMutation({
        mutationFn: async (values: UploadSingleFileInput) => {
            const { file, name, folderId, users, progressTabItemId, abortController, uploadId, key } = values;

            const contentType = file.type || 'application/octet-stream';
            const removeAbortListenerCallbacks: Array<() => void> = [];

            try {
                const abortHandler = async () => {
                    try {
                        await abortMultipartUpload(clientId, { key, uploadId });
                    } catch (error) {
                        console.error('Error aborting multipart upload:', error);
                    }
                };

                abortController.signal.addEventListener('abort', abortHandler);
                removeAbortListenerCallbacks.push(() =>
                    abortController.signal.removeEventListener('abort', abortHandler)
                );

                const totalChunks = Math.ceil(file.size / mergedConfig.chunkSize);
                const partProgress = new Map<number, number>();

                const updateProgress = () => {
                    const totalUploadedBytes = Array.from(partProgress.values()).reduce((sum, val) => sum + val, 0);

                    const totalProgress = Math.min(99, Math.trunc((totalUploadedBytes / file.size) * 100));

                    progressTabCtx.updateItem(progressTabItemId, {
                        progress: totalProgress,
                    });
                };

                const uploadChunk = async (partNumber: number): Promise<UploadPart> => {
                    const start = (partNumber - 1) * mergedConfig.chunkSize;
                    const end = Math.min(start + mergedConfig.chunkSize, file.size);
                    const chunk = file.slice(start, end);

                    const { url } = await getPartUploadUrl(clientId, {
                        key,
                        uploadId,
                        partNumber,
                        contentType,
                    });

                    const response = await axios.put(url, chunk, {
                        headers: { 'Content-Type': contentType },
                        signal: abortController.signal,
                        onUploadProgress: (progressEvent) => {
                            partProgress.set(partNumber, progressEvent.loaded || 0);
                            updateProgress();
                        },
                    });

                    partProgress.set(partNumber, chunk.size);
                    updateProgress();

                    const etag = response.headers['etag'] || response.headers['ETag'];

                    if (!etag) {
                        throw new Error(`No ETag returned for part ${partNumber}`);
                    }

                    return {
                        PartNumber: partNumber,
                        ETag: String(etag).replace(/"/g, ''),
                    };
                };

                const uploadWithConcurrency = async (): Promise<UploadPart[]> => {
                    const results: UploadPart[] = [];
                    const queue = Array.from({ length: totalChunks }, (_, i) => i + 1);

                    const workers = Array.from(
                        {
                            length: Math.min(mergedConfig.maxConcurrentUploads, totalChunks),
                        },
                        async () => {
                            while (queue.length > 0) {
                                const partNumber = queue.shift();
                                if (!partNumber) break;

                                const result = await uploadChunk(partNumber);
                                results.push(result);
                            }
                        }
                    );

                    await Promise.all(workers);
                    return results;
                };

                const parts = await uploadWithConcurrency();
                parts.sort((a, b) => a.PartNumber - b.PartNumber);

                const uploadedFile = await completeMultipartUpload(clientId, {
                    key,
                    uploadId,
                    parts,
                    name,
                    folderId,
                    fileSizeInBytes: file.size,
                    users,
                });

                progressTabCtx.updateItem(progressTabItemId, {
                    progress: 100,
                });

                removeAbortListenerCallbacks.forEach((fn) => fn());

                return uploadedFile;
            } catch (error) {
                removeAbortListenerCallbacks.forEach((fn) => fn());

                try {
                    await abortMultipartUpload(clientId, { key, uploadId });
                } catch (abortError) {
                    console.error('Error aborting multipart upload:', abortError);
                }

                throw error;
            }
        },
    });

    const submit = async (values: SubmitInput) => {
        const { files, name, folderId, users } = values;

        const bulkInitResponse = await initMultipartUploadMutation.mutateAsync({
            files,
            name,
        });

        const uploadPromises = files.map(async (file, index) => {
            const initItem = bulkInitResponse[index];
            const abortController = new AbortController();
            const displayName = files.length > 1 ? file.name : name || file.name;

            const itemData = progressTabCtx.addItem({
                _id: generateUniqueId(),
                title: displayName,
                text: convertFileSize(file.size),
                extension: getFileExtension(file.name),
                onCancel: () => {
                    abortController.abort();
                },
            });

            try {
                const newFile = await uploadFileMutation.mutateAsync({
                    file,
                    name: displayName,
                    folderId,
                    users,
                    progressTabItemId: itemData._id,
                    abortController,
                    uploadId: initItem.uploadId,
                    key: initItem.key,
                });

                onAdd(newFile);

                progressTabCtx.updateItem(itemData._id, {
                    progress: 100,
                    isCompleted: true,
                    onClick: () => navigate('/documents'),
                });

                return {
                    status: 'fulfilled' as const,
                    fileName: displayName,
                };
            } catch (err: any) {
                const isCanceled = err?.message === 'canceled' || err?.code === 'ERR_CANCELED';

                if (isCanceled) {
                    progressTabCtx.removeItem(itemData._id);
                    return {
                        status: 'canceled' as const,
                        fileName: displayName,
                    };
                }

                progressTabCtx.updateItem(itemData._id, {
                    progress: 100,
                    isErrored: true,
                });

                return {
                    status: 'rejected' as const,
                    fileName: displayName,
                    error: err,
                };
            }
        });

        const results = await Promise.all(uploadPromises);

        const failedUploads = results.filter(
            (
                result
            ): result is {
                status: 'rejected';
                fileName: string;
                error: unknown;
            } => result.status === 'rejected'
        );

        if (failedUploads.length > 0) {
            const failedNames = failedUploads.map((item) => item.fileName).join(', ');
            throw new Error(`Failed to upload: ${failedNames}`);
        }
    };

    return {
        isInitializing: initMultipartUploadMutation.isPending,
        isUploading: uploadFileMutation.isPending,
        isLoading: initMultipartUploadMutation.isPending || uploadFileMutation.isPending,
        submit,
    };
};

export default useMultipartUpload;
